// Tipos del cotizador de costos operativos (transportistas). Reflejan el
// contrato del API (`/quotes*`, `/costProfiles*`); ver functions/src/services/quoteEngine.js.

export const QUOTE_STATUSES = ["draft", "sent", "accepted", "rejected", "expired"] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const VEHICLE_CLASSES = [
  "tractocamion_5e",
  "tractocamion_full_9e",
  "torton",
  "rabon",
  "camioneta_3_5",
  "van",
  "otro",
] as const;
export type VehicleClass = (typeof VEHICLE_CLASSES)[number];

export const FUEL_TYPES = ["diesel", "gasoline", "lpg", "cng"] as const;
export type FuelType = (typeof FUEL_TYPES)[number];

export const DRIVER_MODES = ["per_day", "per_km", "per_trip"] as const;
export type DriverMode = (typeof DRIVER_MODES)[number];

export const INSURANCE_MODES = ["per_trip", "pct_of_cargo_value", "per_day"] as const;
export type InsuranceMode = (typeof INSURANCE_MODES)[number];

export const EXTRA_MODES = ["per_trip", "per_day", "per_km"] as const;
export type ExtraMode = (typeof EXTRA_MODES)[number];

export type Currency = "MXN" | "USD";

export interface GeoPointInput {
  lat?: number;
  lng?: number;
  address?: string;
}

export interface TollItem {
  name: string;
  price: number;
  base_price?: number;
  lat?: number;
  lng?: number;
  leg_index?: number;
}

export type TollsSource = "google" | "manual" | "none" | "unavailable";

export interface QuoteTolls {
  total: number;
  base_total?: number;
  source: TollsSource;
  axle_factor: number;
  coverage?: boolean;
  cached?: boolean;
  items: TollItem[];
}

export interface QuoteLine {
  key: string;
  label: string;
  qty: number;
  unit: string;
  unit_cost: number;
  total: number;
  overridden?: boolean;
  custom?: boolean;
  meta?: Record<string, unknown>;
}

export interface QuoteTaxes {
  iva_pct: number;
  retention_pct: number;
  iva_amount?: number;
  retention_amount?: number;
}

export interface QuoteSchedule {
  pickup_at?: string | null;
  delivery_at?: string | null;
  timezone?: string | null;
}

export interface QuoteCargo {
  description?: string | null;
  weight_kg?: number | null;
  volume_m3?: number | null;
  declared_value?: number | null;
  quantity?: number | null;
  unit_type?: string | null;
}

export interface QuoteCustomerSnapshot {
  name?: string | null;
  contact_name?: string | null;
  email?: string | null;
  whatsapp?: string | null;
}

export interface QuoteOverrides {
  margin_pct?: number;
  price?: number;
  remove?: string[];
  lines?: Record<string, { unit_cost?: number; total?: number; label?: string }>;
  custom_lines?: { label: string; qty?: number; unit?: string; unit_cost: number }[];
}

/** Insumos del cálculo (lo que viaja a /quotes/estimate y se guarda en calc_input). */
export interface QuoteCalcInput {
  route_id?: string | null;
  trip_id?: string | null;
  origin?: GeoPointInput | null;
  destination?: GeoPointInput | null;
  waypoints?: GeoPointInput[];
  vehicle_class?: VehicleClass | null;
  unit_id?: string | null;
  cost_profile_id?: string | null;
  schedule?: QuoteSchedule | null;
  cargo?: QuoteCargo | null;
  distance_km?: number | null;
  duration_min?: number | null;
  tolls_override?: { total?: number; items: TollItem[] } | null;
  toll_passes?: string[];
  refresh_tolls?: boolean;
  overrides?: QuoteOverrides;
  taxes?: { iva_pct?: number; retention_pct?: number };
}

/** Datos comerciales (no afectan el cálculo). */
export interface QuoteCommercialInput {
  crm_company_id?: string | null;
  crm_contact_id?: string | null;
  customer_snapshot?: QuoteCustomerSnapshot | null;
  notes?: string | null;
  terms?: string | null;
  valid_until?: string | null;
  show_breakdown?: boolean;
  title?: string | null;
}

export interface QuoteEstimate {
  vehicle_class: VehicleClass;
  currency: Currency;
  distance_km: number;
  duration_min: number;
  days: number;
  tolls: QuoteTolls;
  lines: QuoteLine[];
  subtotal: number;
  overhead_pct: number;
  cost_total: number;
  cost_per_km: number;
  margin_pct: number;
  price: number;
  taxes: QuoteTaxes;
  total_with_tax: number;
  origin?: GeoPointInput | null;
  destination?: GeoPointInput | null;
  waypoints?: GeoPointInput[];
  route_id?: string | null;
  trip_id?: string | null;
  unit_id?: string | null;
  cost_profile_id?: string | null;
  cost_profile_source?: string;
  unit_snapshot?: { id: string; name?: string | null; plate?: string | null } | null;
  route_snapshot?: { id: string; name?: string | null } | null;
  trip_snapshot?: { id: string; title?: string | null } | null;
}

export interface QuoteEstimateInputs {
  route: { id: string; name?: string | null } | null;
  trip: { id: string; title?: string | null } | null;
  vehicle: { id: string; name?: string | null; plate?: string | null } | null;
  profile: { id: string | null; name?: string | null; source?: string };
  tolls_error: { provider: string; status: number | null; message: string } | null;
}

export interface QuotePdfMeta {
  path: string;
  url: string;
  filename?: string;
  show_breakdown?: boolean;
  generated_at?: string;
  stale?: boolean;
}

export interface QuoteSend {
  channel: "email" | "whatsapp";
  to: string;
  at: string;
  message_id?: string | null;
}

export interface Quote extends QuoteEstimate {
  id: string;
  folio: string;
  title?: string | null;
  company_id: string;
  status: QuoteStatus;
  crm_company_id?: string | null;
  crm_contact_id?: string | null;
  customer_snapshot?: QuoteCustomerSnapshot | null;
  schedule?: QuoteSchedule | null;
  cargo?: QuoteCargo | null;
  notes?: string | null;
  terms?: string | null;
  valid_until?: string | null;
  show_breakdown?: boolean;
  calc_input?: QuoteCalcInput;
  pdf?: QuotePdfMeta | null;
  sends?: QuoteSend[];
  cargo_auction_id?: string | null;
  createdAt?: { _seconds: number; _nanoseconds: number } | string;
  updatedAt?: { _seconds: number; _nanoseconds: number } | string;
}

export interface QuoteListParams {
  page?: number;
  limit?: number;
  status?: QuoteStatus | "";
  crm_company_id?: string;
  trip_id?: string;
  route_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}
